import { useEffect } from 'react';

const SCROLL_RETRY_WINDOW_MS = 3000;
const INITIAL_SCROLL_DELAY_MS = 100;
const COMMENT_HIGHLIGHT_COLOR = 'var(--info-thin)';
// shadow gives the highlight some breathing room without moving the layout
const COMMENT_HIGHLIGHT_SHADOW = `0 0 0 4px ${COMMENT_HIGHLIGHT_COLOR}`;

export const getCocCommentAnchorId = (commentId: string): string =>
  `coc-${commentId}`;

export const getCocCommentsBottomAnchorId = (shipmentId: string): string =>
  `coc-comments-bottom-${shipmentId}`;

interface UseScrollToCocCommentArgs {
  commentId?: string | null;
  ready?: boolean;
  scrollToBottom?: boolean;
  shipmentId?: string | null;
}

const clearHighlightOnDocumentClick = (el: HTMLElement) => {
  const onClick = () => {
    el.style.backgroundColor = '';
    el.style.boxShadow = '';
    el.style.borderRadius = '';
    document.removeEventListener('click', onClick);
  };
  document.addEventListener('click', onClick);
};

const scrollToSpecificComment = (commentId: string): boolean => {
  const el = document.getElementById(getCocCommentAnchorId(commentId));
  if (!el) return false;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  el.style.backgroundColor = COMMENT_HIGHLIGHT_COLOR;
  el.style.boxShadow = COMMENT_HIGHLIGHT_SHADOW;
  el.style.borderRadius = '4px';
  clearHighlightOnDocumentClick(el);

  return true;
};

const scrollToCommentsBottom = (shipmentId: string): boolean => {
  const el = document.getElementById(getCocCommentsBottomAnchorId(shipmentId));
  if (!el) return false;

  el.scrollIntoView({ behavior: 'smooth', block: 'end' });
  return true;
};

export const useScrollToCocComment = ({
  commentId,
  ready,
  scrollToBottom,
  shipmentId,
}: UseScrollToCocCommentArgs) => {
  useEffect(() => {
    if (!ready) return;

    const hasSpecificComment = Boolean(commentId);
    const hasBottomTarget = Boolean(scrollToBottom && shipmentId);
    if (!hasSpecificComment && !hasBottomTarget) return;

    let cancelled = false;
    const start = Date.now();

    const tryScroll = () => {
      if (cancelled) return;

      const didScroll = hasSpecificComment
        ? scrollToSpecificComment(String(commentId))
        : scrollToCommentsBottom(String(shipmentId));

      if (didScroll) return;

      if (Date.now() - start < SCROLL_RETRY_WINDOW_MS) {
        requestAnimationFrame(tryScroll);
      }
    };

    const timer = setTimeout(
      () => requestAnimationFrame(tryScroll),
      INITIAL_SCROLL_DELAY_MS
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [commentId, ready, scrollToBottom, shipmentId]);
};
